import { logger } from "@/src/lib/logging/logger";
import { withRetry } from "@/src/lib/retry";
import { customCamelCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { PatientAlertRow } from "@/src/lib/types/tables.types";
import { Group } from "@/src/models/group";
import { PatientAlert, patientAlertShape } from "@/src/models/patient-alert";
import { BaseRepository } from "@/src/repo/base-repository";

export const PATIENT_ALERT_TABLE_NAME = "patient_alert";

export class PatientAlertRepository extends BaseRepository<PatientAlert, PatientAlertRow> {
  constructor() {
    super({
      tableName: PATIENT_ALERT_TABLE_NAME,
      selectClause: "*",
      modelShape: patientAlertShape,
    });
  }
}

export const patientAlertRepository = new PatientAlertRepository();

function mapPatientAlertRows(data: Record<string, unknown>[] | null): PatientAlert[] {
  if (!data || data.length === 0) return [];

  return data.map((row) => {
    const mapped: Record<string, unknown> = {};

    for (const [k, v] of Object.entries(row)) mapped[customCamelCase(k)] = v;

    return mapped as unknown as PatientAlert;
  });
}

export async function findActivePatientAlertsByName(
  patientId: string,
  names: readonly string[],
  group: Group
): Promise<PatientAlert[]> {
  if (!patientId || names.length === 0) return [];

  const today = new Date().toISOString().slice(0, 10);

  const { data, error } = await withRetry("findActivePatientAlertsByName", async () => {
    const supabase = await getSupabaseClient();

    return supabase
      .from(PATIENT_ALERT_TABLE_NAME)
      .select("*")
      .eq("group_id", group.id)
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .or(`expiry_date.is.null,expiry_date.gte.${today}`)
      .in("name", names as string[]);
  });

  if (error) {
    logger.error("findActivePatientAlertsByName error", { patientId, groupId: group.id, error });
    throw new Error(`findActivePatientAlertsByName failed: ${error.message}`);
  }

  return mapPatientAlertRows(data);
}

export async function findActivePatientAlerts(patientId: string, group: Group): Promise<PatientAlert[]> {
  if (!patientId) return [];

  const today = new Date().toISOString().slice(0, 10);

  const { data, error } = await withRetry("findActivePatientAlerts", async () => {
    const supabase = await getSupabaseClient();

    return supabase
      .from(PATIENT_ALERT_TABLE_NAME)
      .select("*")
      .eq("group_id", group.id)
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .or(`expiry_date.is.null,expiry_date.gte.${today}`);
  });

  if (error) {
    logger.error("findActivePatientAlerts error", { patientId, groupId: group.id, error });
    throw new Error(`findActivePatientAlerts failed: ${error.message}`);
  }

  return mapPatientAlertRows(data);
}
